import React, { useState, useEffect } from 'react';
import { Container, Box, Typography, Paper, CircularProgress } from '@mui/material';
import ReservationModal from '../components/ReservationModal';
import { useSettings } from '../contexts/SettingsContext';
import { DirectionsBoat as BoatIcon } from '@mui/icons-material';
import { SettingsProvider } from '../contexts/SettingsContext';
import SessionRecorder from '../components/SessionRecorder';

/**
 * Standalone booking page that shows just a reservation modal
 * Can be accessed directly via /booking URL or via a booking subdomain
 */
const BookingPageContent: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const { marinaSettings, loading } = useSettings();
  const [isBookingSubdomain, setIsBookingSubdomain] = useState(false);

  useEffect(() => {
    // Check if we're on a booking subdomain
    const hostname = window.location.hostname;
    const isSubdomain = hostname.startsWith('booking.') || hostname.startsWith('reserve.') || hostname === 'booking-fllboats.web.app';
    setIsBookingSubdomain(isSubdomain);

    // Open the modal automatically when the page loads
    const timer = setTimeout(() => {
      setModalOpen(true);
    }, 500); // Short delay to ensure page is fully loaded
    
    return () => clearTimeout(timer);
  }, []);

  const handleCloseModal = () => {
    // If on a booking subdomain, don't allow navigation to main site
    if (isBookingSubdomain) {
      // Just close the modal but keep the user on the same page
      setModalOpen(false);
      
      // Optionally reload the page to restart the booking flow
      // window.location.reload();
      
      // Start the timer to reopen the modal after a short delay
      setTimeout(() => {
        setModalOpen(true);
      }, 1000);
      
      return;
    }
    
    // Regular behavior when not on booking subdomain
    // When modal is closed, return to the referrer URL or home page as fallback
    if (document.referrer) {
      window.location.href = document.referrer;
    } else {
      window.location.href = '/';
    }
  };

  return (
    <Container 
      maxWidth={false} 
      disableGutters 
      sx={{ 
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(to bottom, #f5f5f5, #e0e0e0)',
        p: 0
      }}
    >
      <SessionRecorder />
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        width="100%"
      >
        {loading ? (
          <CircularProgress size={60} thickness={4} />
        ) : (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              textAlign: 'center',
              backgroundColor: 'transparent'
            }}
          >
            <BoatIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom>
              {marinaSettings?.businessName || 'Fallen Leaf Marina'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {isBookingSubdomain 
                ? "Welcome to our online booking portal" 
                : "Our online booking portal is loading..."}
            </Typography>
            {isBookingSubdomain && !modalOpen && (
              <Typography 
                variant="body2" 
                sx={{ cursor: 'pointer', color: 'primary.main', mt: 2 }}
                onClick={() => setModalOpen(true)}
              >
                Click here to start a new reservation
              </Typography>
            )}
          </Paper>
        )}
      </Box>

      {/* Reservation Modal that opens automatically */}
      <ReservationModal 
        open={modalOpen} 
        onClose={handleCloseModal}
        disableNavigation={isBookingSubdomain}
      />
    </Container>
  );
};

// Wrap the component with SettingsProvider to ensure it has access to marina settings
const BookingPage: React.FC = () => {
  return (
    <SettingsProvider>
      <BookingPageContent />
    </SettingsProvider>
  );
};

export default BookingPage; 